<script setup lang="ts">
//const user = useUser()
import type { DropdownMenuItem } from '@nuxt/ui';
const accountStore = useAccountStore();
const { data: session, status } = useAuth();
const isLoggedIn = computed(() => status.value === 'authenticated');
const user = computed(() => session.value?.user);
const route = useRoute()
const base = computed(() => route.params.slug ? `/app/${route.params.slug}` : '/app')

const items = computed<DropdownMenuItem[][]>(() => [
  [
    {
      label: user.value?.email ?? '',
      avatar: {
        src: (user.value as any)?.image || undefined,
        alt: ((user.value?.name || user.value?.email || '??')).substring(0, 2).toUpperCase()
      },
      type: 'label'
    }
  ],
  [
    {
      label: 'Account',
      icon: 'i-lucide-user',
      to: `/account`,
    },
    {
      label: 'Billing',
      icon: 'i-lucide-credit-card',
      to: `/pricing`,
    },
    {
      label: 'Settings',
      icon: 'i-lucide-cog',
      kbds: [','],
      to: `/user/settings`,
    },
    // {
    //   label: 'Keyboard shortcuts',
    //   icon: 'i-lucide-monitor'
    // }
  ],
  [
    {
      label: 'Support',
      icon: 'i-lucide-life-buoy',
      onSelect() {
        showSupportModal.value = true;
      }
    },
  ],
  [
    {
      label: 'Logout',
      icon: 'i-lucide-log-out',   
      kbds: ['shift', 'meta', 'q'],
      onSelect() {
        handleLogout()
      }
      // Any of the member function definition formats can be used.
      // onSelect: ()=> {
      //   handleLogout()
      // }
    }
  ]
])

async function handleLogout() {
  const { signOut } = useZitadelSignOut();
  try {
    await $fetch('/api/auth/logout', { method: 'POST' });
    accountStore.signout?.();
  } catch (e: any) {
    const logger = useLogger();
    logger.error(e, 'Sign out error');
  }
  await signOut('/');
}

// ─── Debug credential indicator ──────────────────────────────────────────────
const hasDebugCreds = ref(false);

function checkDebugCreds() {
  if (typeof localStorage === 'undefined') return;
  hasDebugCreds.value = Object.keys(localStorage)
    .filter(k => k.startsWith('debug-creds:'))
    .some(k => {
      try { return Object.values(JSON.parse(localStorage.getItem(k) ?? '{}')).some(Boolean); } catch { return false; }
    });
}

onMounted(() => {
  checkDebugCreds();
  // Re-check when page gains focus (user may have cleared creds in the debugger tab)
  window.addEventListener('focus', checkDebugCreds);
});

// ─── Support Modal ────────────────────────────────────────────────────────────
const showSupportModal = ref(false);

onUnmounted(() => {
  window.removeEventListener('focus', checkDebugCreds);
});
</script>

<template>
  <div v-if="user" class="flex items-center gap-2">
    
      <!-- <RealtimeNotifications /> -->
      <UBadge color="neutral" variant="outline" class="p-2">{{ user?.email }}</UBadge>
      <UDropdownMenu
        :items="items"
        :ui="{
          content: 'w-48'
        }"
      >
        <UTooltip
          :text="hasDebugCreds ? 'Debug credentials stored in localStorage — open any template debugger to clear them' : ''"
        >
          <div class="relative">
            <UButton icon="i-lucide-menu" color="neutral" variant="outline" />
            <span
              v-if="hasDebugCreds"
              class="absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full bg-amber-400 border-2 border-white dark:border-gray-900 animate-pulse"
              aria-label="Debug credentials stored"
            />
          </div>
        </UTooltip>
      </UDropdownMenu>
  
  </div>
  <div v-else>
    <UButton
      to="/auth/signin"
      class=""
      color="secondary"
      
    >
      Login
    </UButton>
  </div>
  <SupportContactModal v-model:open="showSupportModal" />
</template>
